import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StorageKey, StorageService } from '@dta/shared/services/storage/storage.service';
import { TokenApiService } from '@shared/api/api-loop/services';
import {
  ErrorInterceptorName,
  ErrorInterceptorNameValue
} from '@shared/interceptors/common/enum/error-interceptor-name';
import { BaseCachedErrorInterceptor } from '@shared/interceptors/errors/base-cached-error-interceptor';
import { BaseAuthTokensStorageService } from '@shared/shared/auth/data-access/base-auth-tokens-storage.service';
import { TokenType } from '@shared/shared/auth/common/constants/token-type';
import { ToastService } from '@shared/ui/kit/toast/toast.service';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

interface HandleErrorConfig {
  req: HttpRequest<any>;
  next: HttpHandler;
  useCache: boolean;
}

@Injectable()
export class ErrorExpiredAccessTokenInterceptor extends BaseCachedErrorInterceptor {
  override readonly interceptorName: ErrorInterceptorNameValue = ErrorInterceptorName.errorExpiredAccessToken;
  override readonly httpStatusCode: HttpStatusCode = HttpStatusCode.Unauthorized;
  override readonly errorCode: string | null = 'TokenExpired';

  private readonly authTokensStorage: BaseAuthTokensStorageService = inject(BaseAuthTokensStorageService);
  private readonly storageService: StorageService = inject(StorageService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly router: Router = inject(Router);
  private readonly tokenApiService: TokenApiService = inject(TokenApiService);
  private readonly userAuthService: UserAuthService = inject(UserAuthService);

  override intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldBeIgnored(req)) {
      return this.ignoreInterceptor(req, next);
    }
    return next.handle(req).pipe(catchError(err => this.catchError(err, { req, next, useCache: true })));
  }

  protected override catchError(err: HttpErrorResponse, config: HandleErrorConfig): Observable<any> {
    if (!this.shouldCatchError(err)) {
      return throwError(err);
    }
    return this.handleWithCache(err, config);
  }

  protected override handleWithCache(err: HttpErrorResponse, context: HandleErrorConfig): Observable<any> {
    if (!context.useCache) {
      return this.handleRepeatedlyFailedError(err);
    }
    return super.handleWithCache(err, context).pipe(
      switchMap(() => {
        const userId = this.storageService.getItem(StorageKey.activeUserId);
        return this.authTokensStorage.getBearerToken(userId).pipe(take(1));
      }),
      map(bearerToken => {
        if (!bearerToken) {
          return context.req.clone();
        }
        return context.req.clone({
          setHeaders: { Authorization: bearerToken }
        });
      }),
      switchMap(newRequest =>
        context.next
          .handle(newRequest)
          .pipe(catchError(newErr => this.catchError(newErr, { req: newRequest, next: context.next, useCache: false })))
      )
    );
  }

  protected override handleError(err: HttpErrorResponse): Observable<any> {
    const userId = this.storageService.getItem(StorageKey.activeUserId);

    return forkJoin([
      this.authTokensStorage.readToken(userId, TokenType.refresh),
      this.authTokensStorage.readToken(userId, TokenType.access)
    ]).pipe(
      switchMap(([refreshToken, accessToken]) => {
        if (!refreshToken || !accessToken) {
          return this.logoutAndRedirect$(userId);
        }

        return this.tokenApiService
          .Token_RefreshToken(
            { token: { refreshToken, isSyncable: false } },
            { ignoreInterceptors: [ErrorInterceptorName.errorExpiredAccessToken] }
          )
          .pipe(
            switchMap(token =>
              forkJoin([
                token.accessToken
                  ? this.authTokensStorage.storeToken(userId, TokenType.access, token.accessToken)
                  : of(undefined),
                token.refreshToken
                  ? this.authTokensStorage.storeToken(userId, TokenType.refresh, token.refreshToken)
                  : of(undefined)
              ])
            ),
            catchError(() => this.logoutAndRedirect$(userId))
          );
      })
    );
  }

  private logoutAndRedirect$(userId: string): Observable<never> {
    return this.userAuthService.logout$(userId).pipe(
      tap(() => this.router.navigate(['/login'], { replaceUrl: true })),
      switchMap(() => throwError(() => new Error('Authentication failed.')))
    );
  }

  private handleRepeatedlyFailedError(err: HttpErrorResponse): Observable<any> {
    this.toastService.error({
      title: 'Session expired',
      message: this.getErrorMessageFromHttpErrorResponse(err) ?? 'Your session has expired. Please log in again.'
    });
    const userId = this.storageService.getItem(StorageKey.activeUserId);
    return this.userAuthService.logout$(userId).pipe(
      tap(() => this.router.navigate(['/login'], { replaceUrl: true })),
      switchMap(() => this.emptyResponse())
    );
  }
}
